import moment from "moment"
import { FormattedMessage } from "react-intl"
import { round } from "lodash"
import { User } from "~/types/api/UserTypes"
import { Column } from "../interfaces"
import { BooleanCell, DecimalCell, GridUser, ReadableSecondsCell } from "~/components/DataGrid/cellRenderers"
import { NumberHeader } from "~/components/DataGrid/filterHeaders"
import { Message, getUsername } from "~/components/UserName/UserName"
import {
  articleAmountColumn,
  articleNameColumn,
  articleNumberColumn,
  articleGroupNameColumn,
  createdByFullNameColumn,
  createdOnColumn,
  endDateTimeColumn,
  locationNameColumn,
  orderDateColumn,
  orderPositionNumberColumn,
  pendingColumn,
  productionOrderNumberColumn,
  processNameColumn,
  processNumberColumn,
  processCustomerNameColumn,
  processCustomerNumberColumn,
  startDateTimeColumn,
  statusColumn,
  terminationDateColumn,
  remarkColumn,
} from "~/components/DataGrid/columns"

const ColumnsConfigOperations: Column[] = [
  productionOrderNumberColumn,
  articleNumberColumn,
  articleNameColumn,
  articleGroupNameColumn,
  orderPositionNumberColumn,
  {
    id: "valueAdd.valueAddName",
    translationId: "term.operationName",
    accessor: "valueAdd.valueAddName",
    Header: <FormattedMessage id="term.operationName" />,
  },
  locationNameColumn,
  {
    id: "positionNumber",
    translationId: "pages.planningObjects.operations.fields.positionNumber",
    accessor: "positionNumber",
    Filter: NumberHeader,
    Header: <FormattedMessage id="pages.planningObjects.operations.fields.positionNumber" />,
    width: 50,
  },
  {
    id: "setupTime",
    translationId: "term.setupTime",
    accessor: "setupTime",
    Cell: ReadableSecondsCell,
    disableFilters: true,
    Header: <FormattedMessage id="term.setupTime" />,
    width: 60,
  },
  articleAmountColumn,
  {
    id: "duration",
    translationId: "term.duration",
    accessor: "duration",
    Cell: ReadableSecondsCell,
    Header: <FormattedMessage id="term.duration" />,
    width: 50,
    disableFilters: true,
  },
  {
    id: "calculatedDurationHours",
    translationId: "term.specificationTimeInHours",
    accessor: (row: { setupTime: number; duration: number; articleAmount: number; lotSize: number }): number => {
      const calculatedDuration = (row.setupTime || 0) + ((row.duration || 0) * row.articleAmount) / (row.lotSize || 1)

      return round(moment.duration(calculatedDuration, "seconds").asHours(), 2)
    },
    Cell: DecimalCell,
    Filter: NumberHeader,
    Header: <FormattedMessage id="term.specificationTimeInHours" />,
    width: 40,
  },
  {
    id: "resourceGroup.resourceGroupName",
    translationId: "term.resourceGroup",
    accessor: "resourceGroup.resourceGroupName",
    Header: <FormattedMessage id="term.resourceGroup" />,
  },
  {
    id: "resource.resourceName",
    translationId: "term.resource",
    accessor: "resource.resourceName",
    Header: <FormattedMessage id="term.resource" />,
  },
  {
    id: "resourceProgram.resourceProgramName",
    translationId: "term.resourceProgram",
    accessor: "resourceProgram.resourceProgramName",
    Header: <FormattedMessage id="term.resourceProgram" />,
  },
  processCustomerNumberColumn,
  processCustomerNameColumn,
  {
    id: "worker.fullName",
    translationId: "term.worker",
    accessor: "worker",
    Header: <FormattedMessage id="term.worker" />,
    Cell: GridUser,
    export: ({ value }: { value: User }, formatMessage: (arg0: Message) => string): string =>
      getUsername(value, formatMessage),
  },
  {
    id: "status.statusId",
    translationId: "item.status",
    accessor: "status.statusId",
    Header: <FormattedMessage id="item.status" />,
    ...statusColumn("status.statusId"),
    showTooltip: false,
  },
  pendingColumn,
  {
    id: "hasDescription",
    translationId: "common.description",
    accessor: "hasDescription",
    Header: <FormattedMessage id="common.description" />,
    Cell: BooleanCell,
    disableFilters: true,
    disableSortBy: true,
    showTooltip: false,
  },
  processNumberColumn,
  processNameColumn,
  terminationDateColumn,
  orderDateColumn,
  createdOnColumn,
  createdByFullNameColumn,
  startDateTimeColumn,
  endDateTimeColumn,
  remarkColumn,
]

export default ColumnsConfigOperations
